/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HttpPolicySetModule
 */

import {
    Component,
    Type,
} from '@angular/core';

import {
    HTTPHdrActionType,
    IHTTPResponseRule,
    IHTTPRewriteLocHdrAction,
} from 'generated-types';

import { HTTPResponseRule } from 'object-types';
import { L10nService } from '@vmw/ngx-vip';

import { MessageItem }
    from 'ajs/modules/data-model/factories/message-item.factory';

import { RepeatedMessageItem }
    from 'ajs/modules/data-model/factories/repeated-message-item.factory';

import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';

import { HTTPHdrActionConfigItem }
    from 'ajs/modules/http-policy-set/factories/http-hdr-action.config-item.factory';

import { ResponseMatchTargetConfigItem }
    from 'ajs/modules/policies/factories/config-items/response-match-target.config-item.factory';

import { DefaultValues } from 'ajs/modules/core/services/default-values.service';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';

import * as l10n from '../../http-policy-set.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type THTTResponseRulePartial = Omit<IHTTPResponseRule, 'match' | 'hdr_action' | 'loc_hdr_action'>;

interface IHTTPResponseRuleConfig extends THTTResponseRulePartial {
    match?: ResponseMatchTargetConfigItem;
    hdr_action?: RepeatedMessageItem<HTTPHdrActionConfigItem>;
    loc_hdr_action?: MessageItem<IHTTPRewriteLocHdrAction>
}

/**
 * Enums for fields in HTTPResponseRule Message.
 * Used to show dropdown option on the Template.
 */
export const enum HTTPResponseRuleActionFields {
    HDR_ACTION = 'hdr_action',
    LOC_HDR_ACTION = 'loc_hdr_action'
}

/**
 * Default Header Action config value.
 * Used to instantiate hdr_action Message.
 */
const defaultHdrActionValue = {
    action: HTTPHdrActionType.HTTP_REMOVE_HDR,
    hdr: {
        name: '',
        value: {},
    },
};

/**
 * @description
 *
 *   HTTPPolicySet Response Rules Message Item.
 *
 * @author Abhinesh Gour
 */
export class HTTPResponseRuleConfigItem extends
    withFullModalMixin(MessageItem)<IHTTPResponseRuleConfig> {
    public static ajsDependencies = [
        'l10nService',
    ];

    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: HTTPResponseRule,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getAjsDependency_('l10nService');
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Import Lazy Loaded Modal Component.
     */
    /* eslint-disable-next-line */
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const { HTTPResponseRuleModalComponent } = await import(
            /* webpackChunkName: "http-policy-set" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/http-policy-set-modal/components/http-response/http-response-rule-modal/http-response-rule-modal.component'
        );

        return HTTPResponseRuleModalComponent as Type<Component>;
    }

    /**
     * Getter for Header Action config.
     */
    public get headerAction() : RepeatedMessageItem<HTTPHdrActionConfigItem> {
        const { hdr_action: headerActionConfig } = this.config;

        return headerActionConfig;
    }

    /**
     * Returns true if rule has any action configured.
     */
    public hasAnyAction(): boolean {
        return Boolean(this.config.hdr_action?.count || this.config.loc_hdr_action);
    }

    /**
     * Clears Action based on fieldname.
     */
    public clearActionByFieldName(fieldName: string): void {
        if (fieldName) {
            delete this.config[fieldName];
        }
    }

    /**
     * Triggered for add Header Action.
     * Adds 'hdr_action' repeated message with default values.
     */
    public addHeaderAction(): void {
        this.config.hdr_action.add(defaultHdrActionValue);
    }

    /**
     * Triggered for remove Header Action.
     * Removes 'hdr_action' repeated message for given index.
     */
    public removeHeaderAction(index: number): void {
        this.config.hdr_action.remove(index);
    }

    /**
     * Add Action Configs based on fieldname.
     */
    public addActionConfigsByFieldName(fieldName: string): void {
        this.config[fieldName] = this.createChildByField(fieldName);

        // Add repeated msg item for hdr_action with default values.
        if (fieldName === HTTPResponseRuleActionFields.HDR_ACTION) {
            this.addHeaderAction();
        }
    }

    /**
     * @override
     */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IHTTPResponseRuleConfig> {
        const defaultValues: DefaultValues = this.getAjsDependency_('defaultValues');
        const type = this.objectType.toLowerCase();
        const defaultConfig = defaultValues.getDefaultItemConfigByType(type) || {};

        return {
            ...defaultConfig,
            match: null,
            loc_hdr_action: undefined,
        };
    }

    /**
     * @override
     */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.httpResponseRuleModalBreadcrumbTitle);
    }
}
